import { useEffect, useRef } from 'react';
import { navigate, useLocation } from '@gatsbyjs/reach-router';
import qs from 'query-string';
import { useSetRecoilState } from 'recoil';
import { useNodes } from 'reactflow';

import focusedActionId from '~/components/page/Automation/v2/state/focusedActionId';
import useActionIdFromUrl from '../useActionIdFromUrl';
import usePanToActionId from '../usePanToActionId';

/** Higher padding than default pan so adjacent actions stay visible */
export const DEEP_LINK_PAN_PADDING = 1.5;

export const FOCUS_HIGHLIGHT_DURATION_MS = 7000;

/**
 * On load, if `?actionId=` is present and the action exists in the flow,
 * pans to that action and temporarily highlights it.
 * Runs once per page load. Strips the query param after focusing.
 */
const useFocusActionFromUrl = () => {
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  const { actionIdFromUrl, actionExists } = useActionIdFromUrl();

  const hasFocusedRef = useRef(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const setFocusedActionId = useSetRecoilState(focusedActionId);
  const panToAction = usePanToActionId(
    actionExists ? actionIdFromUrl : null,
    DEEP_LINK_PAN_PADDING,
  );
  const renderedNodes = useNodes();

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        global.clearTimeout(highlightTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!actionIdFromUrl || !actionExists || hasFocusedRef.current) return;

    const node = renderedNodes.find(({ id }) => id === actionIdFromUrl);
    if (!node || node.position.x === undefined) return;

    hasFocusedRef.current = true;
    setFocusedActionId(actionIdFromUrl);
    panToAction();

    const { pathname, search, hash } = locationRef.current;
    const searchParams = qs.parse(search);
    delete searchParams.actionId;
    const nextSearch = qs.stringify(searchParams);
    void navigate(
      `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash || ''}`,
      { replace: true },
    );

    highlightTimeoutRef.current = global.setTimeout(() => {
      setFocusedActionId(null);
      highlightTimeoutRef.current = null;
    }, FOCUS_HIGHLIGHT_DURATION_MS);
  }, [
    actionIdFromUrl,
    actionExists,
    renderedNodes,
    panToAction,
    setFocusedActionId,
  ]);
};

export default useFocusActionFromUrl;
